import { useEffect, useState } from 'react';

import { getLocalImage } from '../../lib/imageStore.js';

interface HasCategory {
  categoryId: string;
  hasImage: boolean;
}

/**
 * IndexedDB의 Blob들을 화면에 띄울 수 있는 objectURL로 바꾼다.
 *
 * **만든 URL은 반드시 풀어준다.** objectURL은 탭이 닫힐 때까지 그 Blob을
 * 메모리에 붙잡고 있어서, 목록을 오갈 때마다 사진 몇 MB씩이 쌓인다.
 */
export function useLocalImageUrls(picks: readonly HasCategory[]): ReadonlyMap<string, string> {
  const [urls, setUrls] = useState<ReadonlyMap<string, string>>(new Map());

  // 목록 자체가 아니라 "사진을 가진 카테고리 목록"이 바뀔 때만 다시 만든다.
  // picks 배열은 이름만 고쳐도 새 객체가 되어 매번 다시 읽게 된다.
  const key = picks
    .filter((pick) => pick.hasImage)
    .map((pick) => pick.categoryId)
    .join(',');

  useEffect(() => {
    let cancelled = false;
    const created: string[] = [];

    void (async () => {
      const next = new Map<string, string>();
      for (const categoryId of key === '' ? [] : key.split(',')) {
        const blob = await getLocalImage(categoryId);
        if (blob === null) {
          continue;
        }
        const url = URL.createObjectURL(blob);
        created.push(url);
        next.set(categoryId, url);
      }
      if (cancelled) {
        // 이미 떠난 화면이면 만든 것을 바로 되돌린다
        for (const url of created) {
          URL.revokeObjectURL(url);
        }
        return;
      }
      setUrls(next);
    })();

    return () => {
      cancelled = true;
      for (const url of created) {
        URL.revokeObjectURL(url);
      }
    };
  }, [key]);

  return urls;
}
